import React, { useEffect } from 'react';
import {
  Breadcrumb,
  BackdropCircular
} from 'components';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import NumberFormat from 'react-number-format';
import {
  Button,
  Card,
  Divider,
  Grid,
  TextField,
  Typography
} from '@material-ui/core';
import { Formik } from 'formik';
import * as yup from 'yup';
import { getCustomerRewardsBalanceApi, updateCustomerRewardsBalanceApi } from 'api';


const CustomerRewardBalanceForm = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {id} = useParams<{ id: string }>();

  const {rewardsBalance, loadingRewardsBalance} = useSelector((state: IReduxState) => state.customers);
  const {
    point_balance,
    extension_attributes
  } = rewardsBalance;

  useEffect(() => {
    getCustomerRewardsBalanceApi({
      dispatch,
      sortDirection: 'asc',
      sortField: 'customer_id',
      pageSize: 1,
      currentPage: 1,
      filterConditions: [
        {
          field: 'customer_id',
          value: id,
          conditionType: 'eq'
        }
      ],
      searchConditions: []
    });
  }, []);

  return (
    <div className="flex-grow flex-column relative h-full scroll-y pb-5">
      <div className="mb-sm-30">
        <Breadcrumb
          routeSegments={[
            { name: 'Customers', path: '/customers/list'},
            { name: 'Rewards', path: `/customers/${id}/rewards`},
            { name: 'Edit Balance'}
          ]}
        />
      </div>
      <Card className="mx-10 my-8 overflow-visible">
        <Formik
          initialValues={{
            customer_id: id,
            point_amount: '',
            comment: '',
            expire_after: ''
          }}
          validationSchema={yup.object({
            point_amount: yup.number().required('Field is required'),
            comment: yup.string().required('Field is required'),
          })}
          onSubmit={async (values) => {
            const isBalanceUpdated = await updateCustomerRewardsBalanceApi({dispatch, balance: values});

            if (isBalanceUpdated) {
              history.push(`/customers/${id}/rewards`);
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => (
            <form noValidate autoComplete="off" className="p-8" onSubmit={handleSubmit}>
              <Grid container spacing={5}>
                <Grid item xs={12} sm={9} md={7} lg={5}>
                  <div className="px-10 py-7 w-full text-center border-radius-8 bg-light-primary elevation-z8">
                    <div className="mb-4">
                      <strong>{'Current Balance: '}</strong>
                      {`${point_balance || 0} point${point_balance == 1 ? '' : 's'}`}
                    </div>
                    <div>
                      <strong>{'Total Points Spent: '}</strong>
                      {`${extension_attributes?.total_spending_points || 0}
                       point${extension_attributes?.total_spending_points == 1 ? '' : 's'}`}
                    </div>
                  </div>
                </Grid>
              </Grid>
              <Divider className="my-8" />
              <Grid container spacing={5}>
                <Grid item xs={12} sm={12} md={9} lg={6}>
                  <Card elevation={20} className="p-7">
                    <Typography variant="h4" className="h4 text-primary mb-5">
                      Update Balance:
                    </Typography>
                    <NumberFormat
                      customInput={TextField}
                      fullWidth
                      required
                      fixedDecimalScale
                      decimalScale={0}
                      size="small"
                      variant="outlined"
                      name="point_amount"
                      label="Update Points"
                      value={values.point_amount}
                      onValueChange={(value) => {
                        setFieldValue('point_amount', value.floatValue);
                      }}
                      onBlur={handleBlur}
                      error={touched?.point_amount && Boolean(errors?.point_amount)}
                      helperText={(touched?.point_amount && errors?.point_amount) || 'Enter a positive number to add points, or a negative number to redeem points.'}
                    />
                    <TextField
                      required
                      fullWidth
                      variant="outlined"
                      label="Comment"
                      name="comment"
                      minRows="3"
                      multiline
                      className="my-5"
                      value={values.comment}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched?.comment && Boolean(errors?.comment)}
                      helperText={(touched?.comment && errors?.comment) || 'Describe the reason for the points adjustment.'}
                    />
                    <NumberFormat
                      customInput={TextField}
                      fullWidth
                      allowNegative={false}
                      size="small"
                      variant="outlined"
                      name="expire_after"
                      label="Points expire after"
                      value={values.expire_after}
                      onValueChange={(value) => {
                        setFieldValue('expire_after', value.floatValue);
                      }}
                      onBlur={handleBlur}
                      helperText={'Enter number of days to expiration, e.g. 365 if points expire after 1 year. If empty or 0, points do not expire.'}
                    />
                    <div className="flex justify-end items-center w-full py-5">
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => history.push(`/customers/${id}/rewards`)}
                        className="mr-4 w-full max-w-150"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        className="w-full max-w-150"
                      >
                        Save
                      </Button>
                    </div>
                  </Card>
                </Grid>
              </Grid>
            </form>
          )}
        </Formik>
      </Card>
      <BackdropCircular loading={loadingRewardsBalance} />
    </div>
  );
};

export default CustomerRewardBalanceForm;
